import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Pagination from '../Pagination';
import taskService from '../../services/taskService';
import { FaEye, FaPencilAlt, FaExclamationCircle, FaHourglassHalf, FaCheckCircle, FaSpinner, FaSearch } from 'react-icons/fa';

// Import custom CSS
import '../../styles/taskManagement.css';

const TaskList = ({ projectId = null, filter = null, search = '' }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  
  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(10);
  const [totalTasks, setTotalTasks] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Fetch tasks with proper pagination and filtering
  useEffect(() => {
    const fetchData = async () => {
      await fetchTasks();
    };
    
    // Create a debounce function for search
    const debounce = setTimeout(() => {
      fetchData();
    }, 300);
    
    // Cleanup timer on component unmount
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentPage, filter, search]);
  
  const fetchTasks = async () => {
    try {
      setLoading(true);
      
      // Use taskService with pagination parameters and status filter
      // Also add search parameter if provided
      let response;
      if (projectId) {
        response = await taskService.getTasksByProject(projectId, currentPage, tasksPerPage, filter);
      } else {
        response = await taskService.getAllTasks(currentPage, tasksPerPage, filter);
      }
      
      // Extract tasks and pagination metadata
      const fetchedTasks = response.data;
      
      // Apply client-side filtering for search
      // In a more complete implementation, search would also be handled by backend
      const filteredTasks = fetchedTasks.filter(task => {
        // Filter by search term if search is provided
        const searchMatch = search
          ? task.title.toLowerCase().includes(search.toLowerCase()) ||
            task.project_name.toLowerCase().includes(search.toLowerCase()) ||
            task.assignee_name.toLowerCase().includes(search.toLowerCase())
          : true;
          
        return searchMatch;
      });
      
      setTasks(filteredTasks);
      
      // Update pagination state
      if (response.pagination) {
        setTotalTasks(response.pagination.total);
        setTotalPages(response.pagination.pages);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again later.');
      setLoading(false);
    }
  };
  
  // Change page
  const paginate = (pageNumber) => setCurrentPage(parseInt(pageNumber, 10));
  
  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return {
          className: 'status-badge status-badge-pending',
          icon: <FaExclamationCircle />,
          label: 'Pending'
        };
      case 'in_progress':
        return {
          className: 'status-badge status-badge-in-progress',
          icon: <FaSpinner className="animate-spin" />,
          label: 'In Progress'
        };
      case 'completed':
        return {
          className: 'status-badge status-badge-completed',
          icon: <FaCheckCircle />,
          label: 'Completed'
        };
      default:
        return {
          className: 'status-badge',
          icon: <FaHourglassHalf />,
          label: status
        };
    }
  };
  
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="loading-text">Loading tasks...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <div style={{display: 'flex', alignItems: 'center'}}>
          <div className="error-icon-wrapper">
            <FaExclamationCircle className="error-icon" />
          </div>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  if (tasks.length === 0) {
    return (
      <div className="empty-container">
        <div className="empty-icon-wrapper">
          <FaHourglassHalf className="empty-icon" />
        </div>
        <h3 className="empty-title">No Tasks Found</h3>
        <p className="empty-description">
          {search ? 'Try adjusting your search criteria.' : filter ? `No ${filter.replace('_', ' ')} tasks found.` : 'No tasks available at the moment.'}
          {(currentUser?.role === 'admin' && currentUser?.role === 'manager') && ' Create a new task to get started.'}
        </p>
      </div>
    );
  }
  
  return (
    <div className="task-list-wrapper">
    <div className="task-table-container">
      <div className="custom-scrollbar">
      <table className="task-table">
        {/* Table Header */}
        <thead>
          <tr className="table-header-row">
            <th className="table-header-cell">Title</th>
            <th className="table-header-cell">Project</th>
            <th className="table-header-cell">Assigned To</th>
            {(currentUser?.role === "admin" && currentUser?.role === "manager") && (
              <th className="table-header-cell">Expected Hours</th>
            )}
            <th className="table-header-cell">Status</th>
            <th className="table-header-cell text-center">Actions</th>
          </tr>
        </thead>
  
        {/* Table Body */}
        <tbody className="table-body">
          {tasks.map((task, idx) => {
            const statusInfo = getStatusInfo(task.status);
            return (
              <tr
                key={task.id}
                className={`table-row ${idx % 2 === 0 ? "table-row-even" : "table-row-odd"}`}
                >
                {/* Title */}
                <td className="table-cell">
                  <span className="task-title">{task.title}</span>
                </td>
  
                {/* Project */}
                <td className="table-cell">
                  {task.project_name}
                </td>
  
                {/* Assignee */}
                <td className="table-cell">
                  {task.assignee_name}
                </td>
  
                {/* Expected Hours (Admin Only) */}
                {(currentUser?.role === "admin" && currentUser?.role === "manager") && (
                  <td className="table-cell">
                    {task.expected_hours}
                  </td>
                )}
  
                {/* Status */}
                <td className="table-cell">
                  <span className={statusInfo.className}>
                    {statusInfo.icon}
                    {statusInfo.label}
                  </span>
                </td>
  
                {/* Actions */}
                <td className="table-cell text-center">
                  <div className="action-buttons">
                    <Link
                      to={`/tasks/${task.id}`}
                      className="btn btn-primary btn-icon"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaEye /> <span>View</span>
                    </Link>
  
                    {(currentUser?.role === "admin" && currentUser?.role === "manager") && (
                      <Link
                        to={`/tasks/edit/${task.id}`}
                        className="btn btn-secondary btn-icon"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaPencilAlt /> <span>Edit</span>
                      </Link>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  
    {/* Pagination - show if we have more than one page */}
    {totalPages > 1 && (
      <div className="pagination-container">
        <div className="pagination-wrapper">
          <Pagination
            itemsPerPage={tasksPerPage}
            totalItems={totalTasks}
            paginate={paginate}
            currentPage={currentPage}
            totalPages={totalPages}
          />
        </div>
      </div>
    )}
  </div>
  );
};

export default TaskList;
